import { useState, useEffect } from "react";

type ProgressEntry = {
  step: string;
  at: string;
  [key: string]: any;
};

type Job = {
  id: string;
  status: "queued" | "running" | "succeeded" | "failed";
  progress: ProgressEntry[];
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
};

// PHASE 5 — Operator write actions (create/update/delete_record).
type ActionRow = {
  id: string;
  tool_called: string;
  tool_params: { args?: any; screen_id?: string | null; row_id?: string | null; before?: any } | null;
  claim_summary: string | null;
  created_at: string;
};

const ACTION_LABELS: Record<string, string> = {
  create_record: "Created a record",
  update_record: "Updated a record",
  delete_record: "Deleted a record",
  desktop_permission_granted: "Granted local access",
  desktop_permission_revoked: "Revoked local access",
  schema_evolution_applied: "Applied an Agentic Operating System update",
  gmail_create_draft: "Drafted an email",
  gmail_create_email_draft: "Drafted an email",
  gmail_reply_to_thread: "Replied to email thread",
  gmail_send_draft: "Sent email draft",
  gmail_send_email: "Sent email",
  google_drive_import_csv_rows: "Synced CSV file",
  google_sheets_import_rows: "Synced Google Sheet",
  assignment_run: "Ran assignment",
  media_generate: "Generated media",
  triage_collection: "Ranked priorities",
  project_outlook: "Projected outlook",
};

type Props = {
  workspaceId: string;
  // Agents v2 — when set, the actions list is scoped to this assistant (the
  // default assistant also sees legacy null-assistant actions, server-side).
  assistantId?: string;
};

const STATUS_STYLES: Record<Job["status"], { bg: string; text: string; label: string }> = {
  queued: { bg: "bg-zinc-100", text: "text-zinc-600", label: "Queued" },
  running: { bg: "bg-blue-50", text: "text-blue-700", label: "Running" },
  succeeded: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Succeeded" },
  failed: { bg: "bg-red-50", text: "text-red-700", label: "Failed" },
};

const STEP_LABELS: Record<string, string> = {
  user_resolved: "User resolved",
  org_resolved: "Organization resolved",
  workspace_created: "Workspace created",
  charter_upserted: "Charter saved",
  screens_seeded: "Screens seeded",
  cron_seeded: "Cron jobs seeded",
};

function formatDuration(startedAt: string | null, finishedAt: string | null): string {
  if (!startedAt || !finishedAt) return "—";
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function humanizeToolName(raw: string): string {
  if (ACTION_LABELS[raw]) return ACTION_LABELS[raw];
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function formatClaimSummary(rawSummary: string | null, toolCalled: string): string {
  if (!rawSummary) return "";
  // If the summary is a raw technical sentence like "Operator ran GMAIL_CREATE_EMAIL_DRAFT", clean it.
  if (rawSummary.startsWith("Operator ran ")) {
    const rawAction = rawSummary.replace("Operator ran ", "").trim();
    return `Operator completed ${humanizeToolName(rawAction).toLowerCase()}`;
  }
  return rawSummary;
}

export default function AuditScreen({ workspaceId, assistantId }: Props) {
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [actions, setActions] = useState<ActionRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) {
      setJobs([]);
      return;
    }
    let cancelled = false;
    fetch(`/api/audit/provisioning?workspace_id=${encodeURIComponent(workspaceId)}`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (d?.error) {
          setErr(d.error);
          setJobs([]);
        } else {
          setJobs(Array.isArray(d?.jobs) ? d.jobs : []);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e?.message || "Failed to load activity history");
        setJobs([]);
      });
    const actionsUrl = `/api/audit/actions?workspace_id=${encodeURIComponent(workspaceId)}`
      + (assistantId ? `&assistant_id=${encodeURIComponent(assistantId)}` : "");
    fetch(actionsUrl)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setActions(Array.isArray(d?.actions) ? d.actions : []); })
      .catch(() => { if (!cancelled) setActions([]); });
    return () => { cancelled = true; };
  }, [workspaceId, assistantId]);

  return (
    <div className="px-10 py-8 max-w-4xl">
      <h1 className="text-3xl font-serif text-zinc-900 mb-1">Activity & Audit Log</h1>
      <p className="text-sm text-zinc-500 mb-10">
        Review real-time agent actions, automated tasks, and system events for this workspace.
      </p>

      {actions && actions.length > 0 && (
        <section className="mb-10">
          <h2 className="text-xs uppercase tracking-wider text-zinc-500 mb-4 font-semibold">Agent Actions & Executions</h2>
          <div className="space-y-2">
            {actions.map((a) => {
              const title = humanizeToolName(a.tool_called);
              const summary = formatClaimSummary(a.claim_summary, a.tool_called);
              return (
                <div key={a.id} className="bg-white border border-zinc-200 rounded-lg p-4 flex items-start justify-between gap-4 shadow-sm hover:border-zinc-300 transition-colors">
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-900 font-medium">{title}</div>
                    {summary ? (
                      <div className="text-xs text-zinc-500 mt-0.5">{summary}</div>
                    ) : null}
                  </div>
                  <div className="text-xs text-zinc-400 whitespace-nowrap font-mono">{formatTime(a.created_at)}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-xs uppercase tracking-wider text-zinc-500 mb-4">
          Provisioning jobs
        </h2>

        {jobs === null ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-6 text-sm text-zinc-500">
            Loading…
          </div>
        ) : err ? (
          <div className="bg-white border border-red-200 rounded-lg p-6">
            <div className="text-sm text-red-700">Failed to load audit log</div>
            <div className="text-xs text-zinc-500 mt-1">{err}</div>
          </div>
        ) : jobs.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-6">
            <div className="text-sm text-zinc-700">No provisioning history yet</div>
            <div className="text-xs text-zinc-500 mt-1">
              Records are written for new claims. This workspace was provisioned
              before audit logging was enabled.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => {
              const style = STATUS_STYLES[job.status] || STATUS_STYLES.queued;
              return (
                <div
                  key={job.id}
                  className="bg-white border border-zinc-200 rounded-lg p-5"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded ${style.bg} ${style.text}`}
                      >
                        {style.label}
                      </span>
                      <span className="text-xs text-zinc-500">
                        {formatTime(job.created_at)}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-500">
                      Duration: {formatDuration(job.started_at, job.finished_at)}
                    </div>
                  </div>

                  {Array.isArray(job.progress) && job.progress.length > 0 && (
                    <div className="border-l-2 border-zinc-200 pl-4 space-y-2">
                      {job.progress.map((entry, i) => (
                        <div key={i} className="flex items-baseline gap-3 text-xs">
                          <span className="text-zinc-400 font-mono w-20 shrink-0">
                            {new Date(entry.at).toLocaleTimeString()}
                          </span>
                          <span className="text-zinc-700">
                            {STEP_LABELS[entry.step] || entry.step}
                            {entry.count != null && (
                              <span className="text-zinc-400 ml-1">
                                ({entry.count})
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {job.error && (
                    <div className="mt-3 text-xs bg-red-50 border border-red-200 rounded p-3 text-red-700 font-mono">
                      {job.error}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
